<template>
    <div>
        <SelectField
            v-model="platformName"
            help="help_platform_name"
            label="platform"
            name="platform_name"
            :on-search="loadPlatformNameOptions"
            :options="platformSelectOptions"
        />
    </div>
    <div
        v-if="
            platformName && assetSelectOptions && assetSelectOptions.length > 0
        "
        class="w-full border-b border-gray-200"
    >
        <label class="mb-1 block text-sm font-medium">{{ $t('asset') }}</label>
        <div
            v-for="assetOption in assetSelectOptions"
            :key="assetOption.value"
            class="flex w-full cursor-pointer items-center border border-gray-200 p-2"
        >
            <input
                :id="assetOption.value"
                v-model="asset"
                :value="assetOption"
                class="cursor-pointer"
                type="radio"
                @change="assetSelected"
            />
            <label
                :for="assetOption.value"
                class="ml-2 flex w-full cursor-pointer justify-between"
            >
                <span>{{ assetOption.label }}</span>
                <span>{{ assetOption.total_shares }}</span>
            </label>
        </div>
    </div>
    <div
        v-if="
            platformName &&
            assetSelectOptions &&
            assetSelectOptions.length === 0
        "
        class="p-2"
    >
        <span>{{ $t('no_orders') }}</span>
    </div>
    <div
        v-if="platformName && asset"
        class="mt-2 w-full pt-2"
    >
        <div class="flex w-full justify-between justify-items-stretch">
            <label class="mb-1 block text-sm font-medium">
                {{ $t('transfer_to') }}
            </label>
            <label
                :class="`mb-1 block ${
                    investors.length > 1 ? 'mr-8' : ''
                } text-sm font-medium`"
            >
                {{ $t('amount_of_shares') }}
            </label>
        </div>
        <div
            v-for="(investor, index) of investors"
            :key="index"
            :class="`flex w-full items-center justify-items-stretch ${
                index < investors.length - 1 ? 'border-b border-gray-200' : ''
            } py-3`"
        >
            <div class="flex w-full justify-items-stretch">
                <SelectField
                    v-model="investor.accountId"
                    name="account"
                    class="w-full"
                    :on-search="(search) => loadAccountOptions(search, index)"
                    :options="accountSelectOptions[index]"
                />
                <div>
                    <input
                        v-model="investor.share"
                        type="number"
                        min="1"
                        class="form-input ml-1 w-24 border-gray-200"
                        style="border-radius: 0.25rem; padding: 0.25rem"
                    />
                </div>
            </div>
            <div v-if="investors.length > 1">
                <DeleteButton
                    class="ml-1"
                    @click="removeInvestorRow(index)"
                />
            </div>
        </div>
        <div class="mt-3">
            <CreateButton
                :title="$t('further_transmissions')"
                type="button"
                @click="addNewInvestorRow"
            />
        </div>
    </div>
    <div
        v-if="platformName && asset"
        class="mt-5 w-full"
    >
        <input
            v-model="ticket_link"
            type="text"
            placeholder="Ticket Link ..."
            class="form-input w-full border-gray-200"
            style="border-radius: 0.25rem; padding: 0.25rem"
            @change="emitData"
        />
    </div>
</template>

<script setup lang="ts">
import { SelectField } from '@src/components'
import { Ref, ref, watch } from 'vue'
import { getPlatforms } from '@utils/api/platforms'
import { Account, Platform } from '@src/types'
import {
    getTransmitAccountList,
    getTransmitAssetList,
} from '@utils/api/transmitAssets'
import CreateButton from '@partials/actions/CreateButton.vue'
import DeleteButton from '@partials/actions/DeleteButton.vue'

const props = defineProps<{
    modelValue: Ref<object[]>
    accountUid: string
}>()

const platformSelectOptions = ref<string[]>([])
const accountSelectOptions = ref([])
const assetSelectOptions = ref<
    { label: string; value: string; total_shares: number }[]
>([])
const platformName = ref(null)
const asset = ref(null)
const ticket_link = ref('')
const investors = ref<{ accountId: string; share: number }[]>([
    { accountId: null, share: 1 },
])

const emit = defineEmits(['update:modelValue'])

watch(
    investors.value,
    () => {
        emitData()
    },
    { deep: true }
)

watch(platformName, () => {
    assetSelectOptions.value = []
    if (platformName.value) {
        loadAssetSelectOptions()
    }
})

function emitData(): void {
    emit('update:modelValue', {
        investors: investors.value,
        asset: asset.value,
        ticketLink: ticket_link.value,
    })
}

async function loadPlatformNameOptions(search: string) {
    const initialPlatformsRequest = await getPlatforms({
        'filter[platform_name]': search,
    })
    platformSelectOptions.value = initialPlatformsRequest.data.platforms.map(
        (platform: Platform) => {
            return platform.platform_name as string
        }
    )
}

async function loadAccountOptions(search: string, index: number) {
    if (!platformName.value) {
        return
    }

    const res = await getTransmitAccountList(
        platformName.value,
        props.accountUid,
        search
    )
    accountSelectOptions.value[index] = res.data.accounts.map(
        (account: Account) => {
            return {
                label:
                    account.account_title + ' (' + account.account_type + ')',
                value: account.account_uid,
            }
        }
    )
}

async function loadAssetSelectOptions() {
    if (!platformName.value) {
        return
    }

    const transmitAssetListRequest = await getTransmitAssetList(
        platformName.value,
        props.accountUid
    )
    assetSelectOptions.value = transmitAssetListRequest.data.assets.map(
        (asset) => {
            return {
                label: asset.asset_title as string,
                value: asset.asset_uid as string,
                total_shares: asset.total_shares as number,
            }
        }
    )

    // autoselect if there is only one asset
    if (assetSelectOptions.value.length === 1) {
        asset.value = assetSelectOptions.value[0]
        assetSelected()
    }
}

function assetSelected() {
    emit('update:modelValue', {
        investors: investors.value,
        asset: asset.value,
        ticketLink: ticket_link.value,
    })
}

function addNewInvestorRow() {
    investors.value.push({
        accountId: null,
        share: 1,
    })
}

function removeInvestorRow(index: number) {
    investors.value.splice(index, 1)
}
</script>

<style lang="scss" scoped></style>
